import { type GroupingMode, GROUPING_OPTIONS } from '@/types/grouping';
import { cn } from '@/lib/utils';

interface GroupingSelectorProps {
  mode: GroupingMode;
  onChange: (mode: GroupingMode) => void;
  /** Which grouping modes are available based on the data */
  availableModes?: GroupingMode[];
  className?: string;
  disabled?: boolean;
  /** Show as compact dropdown instead of tabs */
  compact?: boolean;
}

/**
 * Selector for choosing how to group tickers on the Show By page
 */
export function GroupingSelector({
  mode,
  onChange,
  availableModes,
  className = '',
  disabled = false,
  compact = false,
}: GroupingSelectorProps) {
  // Filter options based on available modes
  const options = availableModes
    ? GROUPING_OPTIONS.filter((opt) => availableModes.includes(opt.id))
    : GROUPING_OPTIONS;

  if (compact) {
    // Dropdown mode
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <label
          htmlFor="grouping-select"
          className="text-sm font-medium text-foreground whitespace-nowrap"
        >
          Group by:
        </label>
        <select
          id="grouping-select"
          value={mode}
          onChange={(e) => onChange(e.target.value as GroupingMode)}
          disabled={disabled}
          className={cn(
            'h-9 px-3 rounded-md border border-input bg-background text-sm',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'min-w-[160px]'
          )}
        >
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Tabs/pills mode
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <span className="text-sm font-medium text-foreground">Group by:</span>
      <div
        className="flex flex-wrap gap-1 p-1 bg-muted rounded-lg"
        role="tablist"
        aria-label="Grouping options"
      >
        {options.map((option) => (
          <button
            key={option.id}
            role="tab"
            aria-selected={mode === option.id}
            aria-controls={`grouping-panel-${option.id}`}
            onClick={() => onChange(option.id)}
            disabled={disabled}
            title={option.description}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              mode === option.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-background/50',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default GroupingSelector;
